import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import {
    Grid,
    Card,
    CardContent,
    Typography,
    makeStyles,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem as MuiMenuItem,
    FormControl,
    InputLabel,
    IconButton,
    Tooltip,
    Chip,
    Snackbar,
} from '@material-ui/core'
import Alert from '@mui/material/Alert'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import DeleteIcon from '@mui/icons-material/Delete'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import AddIcon from '@material-ui/icons/Add'

import OrganizationOverviewSidebar from '../Organization/Components/OrganizationOverviewSidebar'
import CapeeshStyledModal from '../../../Components/CapeeshStyledModal'
import { CapeeshColors } from '../../../assets/ColorPalette'
import { apiKeyService } from '../../../_state/services/apiKey.service'

const ApiKeyTypeLabels = {
    0: 'EasyRights',
    1: 'Ukrainian Refugee Add',
    2: 'Self-Serve Organization',
    3: 'Embed Integration',
}

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: '300px',
        marginLeft: 'calc(15% + 25px)',
        marginRight: '25px',
        padding: '20px',
    },
    tableHeaderCell: {
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 500,
        fontFamily: 'Rubik',
        textAlign: 'left',
    },
    tableCell: {
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 400,
        fontFamily: 'Rubik',
    },
    keyText: {
        fontFamily: 'monospace',
        fontSize: '13px',
        color: CapeeshColors.Gray1,
        maxWidth: '180px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    newKeyDisplay: {
        backgroundColor: CapeeshColors.Gray5,
        borderRadius: '8px',
        padding: '16px',
        marginTop: '16px',
        fontFamily: 'monospace',
        fontSize: '14px',
        wordBreak: 'break-all',
        border: `1px solid ${CapeeshColors.Gray3}`,
    },
}))

const AdminApiKeys = (props) => {
    const classes = useStyles()
    const { organization } = props
    const organizationId = organization.selectedOrganization

    const [apiKeys, setApiKeys] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    const [showCreateDialog, setShowCreateDialog] = useState(false)
    const [createKeyType, setCreateKeyType] = useState(3)
    const [createStartDate, setCreateStartDate] = useState(new Date().toISOString().split('T')[0])
    const [createEndDate, setCreateEndDate] = useState(
        new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    )
    const [creating, setCreating] = useState(false)
    const [newlyCreatedKey, setNewlyCreatedKey] = useState(null)

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [keyToDelete, setKeyToDelete] = useState(null)

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

    const fetchApiKeys = async () => {
        if (!organizationId) return
        setLoading(true)
        try {
            const response = await apiKeyService.getApiKeysForOrganization(organizationId, true)
            setApiKeys(response.data)
            setError(null)
        } catch (err) {
            setError('Failed to load API keys')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchApiKeys()
    }, [organizationId])

    const handleCreateKey = async () => {
        setCreating(true)
        try {
            const dto = {
                apiKeyType: createKeyType,
                startDate: new Date(createStartDate).toISOString(),
                endDate: new Date(createEndDate).toISOString(),
            }
            const response = await apiKeyService.createApiKey(organizationId, dto)
            setNewlyCreatedKey(response.data.key)
            setSnackbar({ open: true, message: 'API key created successfully', severity: 'success' })
            fetchApiKeys()
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to create API key', severity: 'error' })
        } finally {
            setCreating(false)
        }
    }

    const handleDeleteKey = async () => {
        if (!keyToDelete) return
        try {
            await apiKeyService.deleteApiKey(organizationId, keyToDelete.id)
            setSnackbar({ open: true, message: 'API key deleted', severity: 'success' })
            fetchApiKeys()
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to delete API key', severity: 'error' })
        } finally {
            setShowDeleteConfirm(false)
            setKeyToDelete(null)
        }
    }

    const handleCopyKey = (key) => {
        navigator.clipboard.writeText(key)
        setSnackbar({ open: true, message: 'API key copied to clipboard', severity: 'success' })
    }

    const getKeyStatus = (apiKey) => {
        const now = new Date()
        const start = new Date(apiKey.startDate)
        const end = new Date(apiKey.endDate)
        if (now < start) return { label: 'Not Started', color: CapeeshColors.Gray2 }
        if (now > end) return { label: 'Expired', color: CapeeshColors.DeepRed }
        return { label: 'Active', color: CapeeshColors.Green }
    }

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        })
    }

    const handleCloseCreateDialog = () => {
        setShowCreateDialog(false)
        setNewlyCreatedKey(null)
        setCreateKeyType(3)
    }

    return (
        <div className={classes.root}>
            {showDeleteConfirm && keyToDelete && (
                <CapeeshStyledModal
                    open={showDeleteConfirm}
                    textTitle={'Delete API Key'}
                    textLine1={
                        'Are you sure you want to delete this API key? Any integrations using this key will stop working immediately.'
                    }
                    onButton2={() => handleDeleteKey()}
                    onButton1={() => {
                        setShowDeleteConfirm(false)
                        setKeyToDelete(null)
                    }}
                    button1Text={'Cancel'}
                    button1Variant={'outlined'}
                    button2Text={'Delete'}
                    onClose={() => {
                        setShowDeleteConfirm(false)
                        setKeyToDelete(null)
                    }}
                />
            )}

            <OrganizationOverviewSidebar path={props.computedMatch?.path} />

            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <Typography variant="h4" style={{ marginTop: '25px' }}>
                        API Keys
                    </Typography>
                </Grid>
                <Grid
                    item
                    xs={6}
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'baseline',
                    }}>
                    <Button
                        color="primary"
                        variant="contained"
                        style={{ marginTop: '15px', marginRight: '15px' }}
                        startIcon={<AddIcon />}
                        onClick={() => setShowCreateDialog(true)}>
                        Create API Key
                    </Button>
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="body2" style={{ marginTop: '10px' }}>
                        Manage API keys for this organization. API keys are used for external integrations such as
                        embedded Capeesh views in partner platforms.
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            {loading && <Typography>Loading...</Typography>}
                            {error && (
                                <Typography style={{ color: CapeeshColors.DeepRed }}>{error}</Typography>
                            )}
                            {!loading && !error && apiKeys.length === 0 && (
                                <Typography style={{ color: CapeeshColors.Gray1, padding: '20px', textAlign: 'center' }}>
                                    No API keys found for this organization. Create one to get started with integrations.
                                </Typography>
                            )}
                            {!loading && !error && apiKeys.length > 0 && (
                                <TableContainer
                                    sx={{
                                        minWidth: 400,
                                        backgroundColor: CapeeshColors.Gray5,
                                        borderRadius: '16px',
                                    }}>
                                    <Table size="small" style={{ marginTop: '10px', marginBottom: '10px' }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell width="25%">
                                                    <Typography className={classes.tableHeaderCell}>Key</Typography>
                                                </TableCell>
                                                <TableCell width="18%">
                                                    <Typography className={classes.tableHeaderCell}>Type</Typography>
                                                </TableCell>
                                                <TableCell width="12%">
                                                    <Typography className={classes.tableHeaderCell}>Status</Typography>
                                                </TableCell>
                                                <TableCell width="15%">
                                                    <Typography className={classes.tableHeaderCell}>Start Date</Typography>
                                                </TableCell>
                                                <TableCell width="15%">
                                                    <Typography className={classes.tableHeaderCell}>End Date</Typography>
                                                </TableCell>
                                                <TableCell width="15%" align="right">
                                                    <Typography className={classes.tableHeaderCell}>Actions</Typography>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {apiKeys.map((apiKey) => {
                                                const status = getKeyStatus(apiKey)
                                                return (
                                                    <TableRow key={apiKey.id}>
                                                        <TableCell>
                                                            <Typography className={classes.keyText}>
                                                                {apiKey.key.substring(0, 8)}...{apiKey.key.substring(apiKey.key.length - 4)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography className={classes.tableCell}>
                                                                {ApiKeyTypeLabels[apiKey.apiKeyType] || `Type ${apiKey.apiKeyType}`}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={status.label}
                                                                size="small"
                                                                style={{
                                                                    backgroundColor: status.color,
                                                                    color: 'white',
                                                                    fontWeight: 500,
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography className={classes.tableCell}>
                                                                {formatDate(apiKey.startDate)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography className={classes.tableCell}>
                                                                {formatDate(apiKey.endDate)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Tooltip title="Copy full key">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleCopyKey(apiKey.key)}>
                                                                    <ContentCopyIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Delete key">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => {
                                                                        setKeyToDelete(apiKey)
                                                                        setShowDeleteConfirm(true)
                                                                    }}>
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                )
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Dialog open={showCreateDialog} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Create New API Key</DialogTitle>
                <DialogContent>
                    {!newlyCreatedKey ? (
                        <Grid container spacing={2} style={{ marginTop: '8px' }}>
                            <Grid item xs={12}>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel>API Key Type</InputLabel>
                                    <Select
                                        value={createKeyType}
                                        onChange={(e) => setCreateKeyType(e.target.value)}
                                        label="API Key Type">
                                        <MuiMenuItem value={0}>EasyRights</MuiMenuItem>
                                        <MuiMenuItem value={1}>Ukrainian Refugee Add</MuiMenuItem>
                                        <MuiMenuItem value={2}>Self-Serve Organization</MuiMenuItem>
                                        <MuiMenuItem value={3}>Embed Integration</MuiMenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Start Date"
                                    type="date"
                                    fullWidth
                                    variant="outlined"
                                    value={createStartDate}
                                    onChange={(e) => setCreateStartDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="End Date"
                                    type="date"
                                    fullWidth
                                    variant="outlined"
                                    value={createEndDate}
                                    onChange={(e) => setCreateEndDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    ) : (
                        <div>
                            <Typography variant="body1" style={{ fontWeight: 500 }}>
                                Your new API key has been created. Copy it now -- you will not be able to see the full key again.
                            </Typography>
                            <div className={classes.newKeyDisplay}>{newlyCreatedKey}</div>
                            <Button
                                variant="outlined"
                                color="primary"
                                style={{ marginTop: '12px' }}
                                startIcon={<ContentCopyIcon />}
                                onClick={() => handleCopyKey(newlyCreatedKey)}>
                                Copy Key
                            </Button>
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    {!newlyCreatedKey ? (
                        <>
                            <Button onClick={handleCloseCreateDialog}>Cancel</Button>
                            <Button
                                onClick={handleCreateKey}
                                color="primary"
                                variant="contained"
                                disabled={creating}>
                                {creating ? 'Creating...' : 'Create'}
                            </Button>
                        </>
                    ) : (
                        <Button onClick={handleCloseCreateDialog} color="primary" variant="contained">
                            Done
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    )
}

const mapStateToProps = (state) => {
    const { organization, user } = state
    return {
        organization,
        user,
    }
}

export default connect(mapStateToProps)(AdminApiKeys)
